import React from "react";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import { PublicPDFViewer } from "@components/pdf/PublicPDFViewer";
import Login from "@pages/Auth/Login";
import FillForm from "@pages/FillForm/FillForm";
import ThankYou from "@pages/FillForm/ThankYou";
import BasicInformation from "@pages/FormBuilder/BasicInformation";
import FormQuestions from "@pages/FormBuilder/FormQuestions";
import FormReview from "@pages/FormBuilder/FormReview";
import EmailTemplateModal from "@pages/Modals/EmailTemplateModal";
import Patients from "@pages/Patient/Patients";
import NewPatient from "@pages/Patient/NewPatient";
import PatientProfile from "@pages/Patient/PatientProfile";
import VoicePage from "@pages/VoicePage";
import ResetPassword from "@pages/Auth/ResetPassword";
import ForgotPassword from "@pages/Auth/ForgotPassword";
import AuthLayout from "@pages/Auth/AuthLayout";
import OwnerRoute from "./OwnerRoute";
import UsersPermissionsPage from "@pages/Users/UsersPermissionsPage";
import AdminRoute from "./AdminRoute";
import ReactivationRoute from "./ReactivationRoute";
import AdminClinics from "@pages/Users/AdminClinics";
import AdminSignup from "@pages/Auth/AdminSignup";
import AuditLogsPage from "@pages/Audit/AuditLogsPage";
import AdminAgentQuestions from "@pages/Users/AdminAgentQuestions";
import AdminAgentTests from "@pages/Users/AdminAgentTests";
import AdminCallMatching from "@pages/Users/AdminCallMatching";
import SmsLeads from "@pages/SmsLeads/SmsLeads";
import SmsLeadDetail from "@pages/SmsLeads/SmsLeadDetail";
import LeadForm from "@pages/SmsLeads/LeadForm";
import ReactivationPage from "@pages/ReactivationPage";
import { ClinicScopeProvider } from "@context/ClinicContext";

/** The one place an admin's selected clinic lives, over every signed-in page. */
const ClinicScopeLayout = () => (
  <ClinicScopeProvider>
    <Outlet />
  </ClinicScopeProvider>
);

const AppRoutes = ({ isAuthenticated, setIsAuthenticated }) => (
  <Routes>
    {/* Public Routes */}
    <Route
      path="/populateEmailForm"
      element={<FillForm isPatientAccess={true} />}
    />
    <Route path="/public/pdf-viewer/:fileId" element={<PublicPDFViewer />} />
    <Route path="/thank-you" element={<ThankYou />} />
    <Route path="/sms-form" element={<LeadForm />} />

    {isAuthenticated ? (
      <Route element={<ClinicScopeLayout />}>
        <Route path="/" element={<Patients />} />
        <Route path="/patient-profile" element={<PatientProfile />} />
        <Route path="/new-patient" element={<NewPatient />} />
        <Route path="/new-form" element={<BasicInformation />} />
        <Route path="/form-questions" element={<FormQuestions />} />
        <Route path="/form-review" element={<FormReview />} />
        <Route path="/fill-form" element={<FillForm />} />
        <Route path="/send-form" element={<EmailTemplateModal />} />
        <Route path="/voice" element={<VoicePage />} />
        <Route
          path="/users-permissions"
          element={
            <OwnerRoute>
              <UsersPermissionsPage />
            </OwnerRoute>
          }
        />
        <Route
          path="/admin/clinics"
          element={
            <AdminRoute>
              <AdminClinics />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/signup"
          element={
            <AdminRoute>
              <AdminSignup />
            </AdminRoute>
          }
        />
        <Route
          path="/audit-logs"
          element={
            <OwnerRoute>
              <AuditLogsPage />
            </OwnerRoute>
          }
        />
        <Route
          path="/admin/agent-questions"
          element={
            <AdminRoute>
              <AdminAgentQuestions />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/agent-tests"
          element={
            <AdminRoute>
              <AdminAgentTests />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/call-matching"
          element={
            <AdminRoute>
              <AdminCallMatching />
            </AdminRoute>
          }
        />
        <Route
          path="/sms-leads"
          element={
            <AdminRoute>
              <SmsLeads />
            </AdminRoute>
          }
        />
        <Route
          path="/sms-leads/:leadId"
          element={
            <AdminRoute>
              <SmsLeadDetail />
            </AdminRoute>
          }
        />
        <Route
          path="/reactivation"
          element={
            <ReactivationRoute>
              <ReactivationPage />
            </ReactivationRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" />} />
      </Route>
    ) : (
      <>
        <Route element={<AuthLayout />}>
          <Route
            path="/login"
            element={<Login setIsAuthenticated={setIsAuthenticated} />}
          />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="*" element={<Navigate to="/login" />} />
        </Route>
      </>
    )}
  </Routes>
);

export default AppRoutes;
